//@flow
import {useCallback, useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import {useSelector} from 'react-redux';

import {endpoints} from '../api';
import {getMultichatRealTimeAuthenticationTokenService} from '../promises';
import {useRealTimeListener} from '../../../real-time/utils';

import {useActions} from '../../../../utils/globalStore';
import {actions as ChatActions} from '../../chat/duck';
import {actions as FunnelActions} from '../../funnels/duck';
import {actions as RealTimeMultichatActions} from '../duck';
import {
  useCurrentConversation,
  useFilterConversations,
} from '../../chat/utils/conversation';
import {useUser} from '../../../auth/utils';
import {
  conversationMessageAdapter,
  mapRawToReaction,
} from '../../chat/utils/message-adapter';

import type {RealTimeMultichatStatus} from '../duck';
import type {
  ChatServiceType,
  ContactInformation,
  Conversation,
  MessageResponse,
} from '../../../../entities';

import type {RootState} from '../../../';
import {useNotificationSound} from '../../../../utils/react/useNotificationSound';

import newMessageSound from '../../../../assets/sounds/tiendachat-new-message-notification-sound.mp3';
import newBotMessageSound from '../../../../assets/sounds/tiendachat-new-bot-message-notification-sound.mp3';

type MessageEventData = {
  contact: {
    type: 'crm-conversation-contact',
    crmConversationContactId: number,
    identifier: string,
    contactInformation: ContactInformation,
    createdAt: number,
    updatedAt: number,
    ravenService: ChatServiceType,
  },
  conversation: Conversation,
  message: MessageResponse,
};

export function useRealTimeMultichatActions() {
  const actions = useActions(RealTimeMultichatActions);

  const setToken = useCallback(
    (token: string) => {
      actions.setToken(token);
    },
    [actions]
  );

  const setError = useCallback(
    (error: string | null) => {
      actions.setError(error);
    },
    [actions]
  );

  const setListening = useCallback(
    (listening: boolean) => {
      actions.setListening(listening);
    },
    [actions]
  );

  const setConnecting = useCallback(
    (connecting: boolean) => {
      actions.setConnecting(connecting);
    },
    [actions]
  );

  const setConnectionStatus = useCallback(
    (status: RealTimeMultichatStatus) => {
      actions.setConnectionStatus(status);
    },
    [actions]
  );

  const retryConnection = useCallback(() => {
    getMultichatRealTimeAuthenticationTokenService()
      .then(({token}) => {
        setToken(token);
      })
      .catch(() => {
        setError(
          'Error al conectarse al servidor de mensajes, recarga la página'
        );
      });
  }, [setError, setToken]);

  return {
    setToken,
    setError,
    setListening,
    setConnecting,
    setConnectionStatus,
    retryConnection,
  };
}

const realTimeMultichatSelector = (state: RootState) => state.realTimeMultichat;

export function useRealTimeMultichat() {
  const {error, listening, token, connecting, status} = useSelector(
    realTimeMultichatSelector
  );

  return {error, listening, token, connecting, status};
}

const isAtChatEndSelector = (state: RootState) => state.chat.isAtChatEnd;

export function useListenMultichat() {
  const user = useUser();

  const isAtChatEnd = useSelector(isAtChatEndSelector);
  const playNewMessageSound = useNotificationSound(newMessageSound);
  const playNewBotMessageSound = useNotificationSound(newBotMessageSound);

  const addMessage = useActions(ChatActions.addNewMessages);
  const addReactionMessage = useActions(
    ChatActions.addConversationReactionMessage
  );
  const upsertFunnelConversation = useActions(
    FunnelActions.upsertFunnelConversation
  );
  const updateConversations = useActions(ChatActions.updateStoreConversation);
  const {currentConversation} = useCurrentConversation();
  const {tags} = useFilterConversations();

  const {error, listening, token} = useRealTimeMultichat();

  const location = useLocation();

  const isInFunnelPage = location.pathname.includes('funnels');

  const {
    setError,
    setToken,
    setListening,
    setConnecting,
    setConnectionStatus,
  } = useRealTimeMultichatActions();

  useEffect(() => {
    if (!user) return;

    getMultichatRealTimeAuthenticationTokenService()
      .then(({token}) => {
        setToken(token);
      })
      .catch(() => {
        setError(
          'Error al conectarse al servidor de mensajes, recarga la página'
        );
      });
  }, [setError, setToken, user]);

  const onMessage = useCallback(
    data => {
      if (!data.Message) return;

      const messageData: MessageEventData = JSON.parse(data.Message);

      const isInCurrentConversation =
        currentConversation &&
        currentConversation.id === messageData.conversation.id;

      const {crmCustomer, ...conversationData} = messageData.conversation;

      if ((tags && Array.isArray(tags) && tags.length === 0) || !tags) {
        updateConversations({
          ...conversationData,
          read: isInCurrentConversation,
        });
      }

      if (isInFunnelPage) {
        upsertFunnelConversation({
          ...conversationData,
          read: !!isInCurrentConversation,
        });
      }
      const messageConversation = messageData.message;
      const conversation = messageData.conversation;

      if (!messageConversation.fromMe) {
        if (conversation.requiresAssistance) {
          playNewBotMessageSound();
        } else {
          playNewMessageSound();
        }
      }

      if (isInCurrentConversation && isAtChatEnd) {
        const isReaction = messageConversation.messageType === 'REACTION';
        if (isReaction) {
          const reactionMessage = mapRawToReaction(messageConversation);

          if (!reactionMessage) return;
          addReactionMessage(reactionMessage);
        } else {
          conversationMessageAdapter(messageConversation).then(message => {
            if (!message) return;
            addMessage([message]);
          });
        }
      }
    },
    [
      currentConversation,
      tags,
      isInFunnelPage,
      isAtChatEnd,
      updateConversations,
      upsertFunnelConversation,
      playNewBotMessageSound,
      playNewMessageSound,
      addReactionMessage,
      addMessage,
    ]
  );

  const onError = useCallback(
    error => {
      setError(
        'Error al conectarse al servidor de mensajes, recarga la página'
      );
    },
    [setError]
  );

  const clearError = () => setError(null);

  const {connecting, open} = useRealTimeListener(
    token && `${endpoints.MULTICHAT_STREAM}?t=${token}`,
    {
      onMessage,
      onError,
    }
  );

  useEffect(() => {
    setListening(open && !connecting);
  }, [connecting, open, setListening]);

  useEffect(() => {
    setConnecting(connecting);
  }, [connecting, setConnecting]);

  useEffect(() => {
    if (error) setConnectionStatus('DISCONNECTED');
    if (listening) setConnectionStatus('CONNECTED');
    if (connecting) setConnectionStatus('CONNECTING');
  }, [connecting, error, listening, setConnectionStatus]);

  return {
    error,
    listening,
    clearError,
  };
}
